// API client de agentes personalizados (custom assistants).
// Backend: mcp_django/apps/assistants/ — endpoints REST con auth JWT.
// Mismo patrón/proxy que src/modules/documents/api.ts.

import { ensureFreshToken } from '@/lib/tokenRefresh';

import type {
  AssistantCreateIn,
  AssistantOut,
  AssistantTab,
  AssistantUpdateIn,
} from './types';

const DJANGO_PROXY = '/django-api';
const BASE = '/api/v1/admin/assistants';

async function authHeaders(): Promise<Record<string, string>> {
  await ensureFreshToken();
  const token = localStorage.getItem('auth_token');
  return { Authorization: `Bearer ${token ?? ''}` };
}

async function asJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`HTTP ${res.status}: ${body}`);
  }
  if (res.status === 204) return null as T;
  return res.json() as Promise<T>;
}

// ── List ──────────────────────────────────────────────
export async function listAssistants(tab: AssistantTab = 'all'): Promise<AssistantOut[]> {
  const res = await fetch(`${DJANGO_PROXY}${BASE}/?tab=${tab}`, {
    headers: await authHeaders(),
  });
  const data = await asJson<{ items: AssistantOut[] } | AssistantOut[]>(res);
  // El endpoint devuelve lista plana; defensivo por si se pagina en el futuro.
  return Array.isArray(data) ? data : data.items ?? [];
}

// ── Create ────────────────────────────────────────────
export async function createAssistant(input: AssistantCreateIn): Promise<AssistantOut> {
  const res = await fetch(`${DJANGO_PROXY}${BASE}/`, {
    method: 'POST',
    headers: { ...(await authHeaders()), 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return asJson<AssistantOut>(res);
}

// ── Update ────────────────────────────────────────────
export async function updateAssistant(
  id: string,
  patch: AssistantUpdateIn,
): Promise<AssistantOut> {
  const res = await fetch(`${DJANGO_PROXY}${BASE}/${id}`, {
    method: 'PUT',
    headers: { ...(await authHeaders()), 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  return asJson<AssistantOut>(res);
}

// ── Delete (soft) ─────────────────────────────────────
export async function deleteAssistant(id: string): Promise<void> {
  const res = await fetch(`${DJANGO_PROXY}${BASE}/${id}`, {
    method: 'DELETE',
    headers: await authHeaders(),
  });
  await asJson<null>(res);
}
